import type { LucideIcon } from 'lucide-react'
import {
  ClipboardList, Sparkles, Wrench, ClipboardCheck, Truck, BellRing,
  Inbox, ArrowUpCircle, Calendar, ShoppingCart, Box, Tag, MapPin, Warehouse,
  User, Shield, GitBranch, Copy, LayoutGrid, Megaphone, Zap,
} from 'lucide-react'

export type CreateKind = 'flyout' | 'dialog' | 'route'
export type CreateGroup = 'tasks' | 'requests' | 'inventory' | 'admin'

export interface CreateMenuItem {
  /** 唯一 key，CreateMenuHost 用它 switch 渲染对应组件 */
  id: string
  group: CreateGroup
  /** i18n key（namespace 'create'），如 'item.task' */
  labelKey: string
  icon: LucideIcon
  /** 创建权限码，用于 <Can> / hasPermission 门控 */
  perm: string
  kind: CreateKind
  /** kind === 'route' 时的跳转目标 */
  route?: string
  /** Task 项预选的 task type（透传给 TaskCreateForm initialData.taskType） */
  taskType?: string
}

/** 分组在面板里的固定列顺序 */
export const GROUP_ORDER: CreateGroup[] = ['tasks', 'requests', 'inventory', 'admin']

export const CREATE_ITEMS: CreateMenuItem[] = [
  // ---- Tasks ----
  // Quick create：简化版的 Report issue / Create task（Web 说明 2026-10-19-intake-ui-web.md），
  // 先试用；不顶替下面原来的 Task 与 Requests 里的 Service Request。
  { id: 'quick_create', group: 'tasks', labelKey: 'item.quickCreate', icon: Zap, perm: 'pms:intent:create', kind: 'flyout' },
  { id: 'task', group: 'tasks', labelKey: 'item.task', icon: ClipboardList, perm: 'pms:task:create', kind: 'flyout' },
  { id: 'task.cleaning', group: 'tasks', labelKey: 'item.taskCleaning', icon: Sparkles, perm: 'pms:task:create', kind: 'flyout', taskType: 'cleaning' },
  { id: 'task.maintenance', group: 'tasks', labelKey: 'item.taskMaintenance', icon: Wrench, perm: 'pms:task:create', kind: 'flyout', taskType: 'maintenance' },
  { id: 'task.inspection', group: 'tasks', labelKey: 'item.taskInspection', icon: ClipboardCheck, perm: 'pms:task:create', kind: 'flyout', taskType: 'inspection' },
  { id: 'task.delivery', group: 'tasks', labelKey: 'item.taskDelivery', icon: Truck, perm: 'pms:task:create', kind: 'flyout', taskType: 'delivery' },
  { id: 'task.guest_service', group: 'tasks', labelKey: 'item.taskGuestService', icon: BellRing, perm: 'pms:task:create', kind: 'flyout', taskType: 'guest_service' },
  // ---- Requests & Guest ----
  { id: 'intent', group: 'requests', labelKey: 'item.intent', icon: Inbox, perm: 'pms:intent:create', kind: 'flyout' },
  { id: 'upsell', group: 'requests', labelKey: 'item.upsell', icon: ArrowUpCircle, perm: 'pms:upsell_request:create', kind: 'flyout' },
  { id: 'reservation', group: 'requests', labelKey: 'item.reservation', icon: Calendar, perm: 'pms:reservation:create', kind: 'flyout' },
  // ---- Inventory & Purchase ----
  { id: 'purchase', group: 'inventory', labelKey: 'item.purchase', icon: ShoppingCart, perm: 'procurement:purchase:create', kind: 'dialog' },
  { id: 'asset', group: 'inventory', labelKey: 'item.asset', icon: Box, perm: 'asset-mgr:asset:create', kind: 'dialog' },
  { id: 'product', group: 'inventory', labelKey: 'item.product', icon: Tag, perm: 'asset-mgr:product:create', kind: 'flyout' },
  { id: 'place.company', group: 'inventory', labelKey: 'item.place.company', icon: Warehouse, perm: 'asset-mgr:location:create', kind: 'dialog' },
  { id: 'place.virtual', group: 'inventory', labelKey: 'item.place.virtual', icon: MapPin, perm: 'asset-mgr:location:create', kind: 'dialog' },
  // ---- Admin ----
  { id: 'user', group: 'admin', labelKey: 'item.user', icon: User, perm: 'system:user:create', kind: 'flyout' },
  { id: 'role', group: 'admin', labelKey: 'item.role', icon: Shield, perm: 'system:role:create', kind: 'route', route: '/roles-permissions' },
  { id: 'workflow', group: 'admin', labelKey: 'item.workflow', icon: GitBranch, perm: 'pms:workflow:create', kind: 'route', route: '/workflows/new' },
  { id: 'task_template', group: 'admin', labelKey: 'item.taskTemplate', icon: Copy, perm: 'pms:task_template:create', kind: 'route', route: '/task-templates/new' },
  { id: 'task_subtype', group: 'admin', labelKey: 'item.taskSubtype', icon: LayoutGrid, perm: 'pms:task_subtype:create', kind: 'route', route: '/catalogs/task-subtypes' },
  { id: 'announcement', group: 'admin', labelKey: 'item.announcement', icon: Megaphone, perm: 'system:announcement:manage', kind: 'dialog' },
]

export function findCreateItemById(id: string): CreateMenuItem | undefined {
  return CREATE_ITEMS.find((it) => it.id === id)
}
